import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import {
    fetchClientHistory,
    fetchClientHistoryExtras,
    fetchClientProfile,
    fetchClientProfileCdp,
    fetchClientProfileRelated,
} from "../../../../lib/api";
import { findProfileInDataset, profileIdFromPartnerId } from "../../../../lib/clientProfileId";
import type { LoyaltyRow, MasterProfile } from "../../../../types";
import { buildClientDetailSections, getProfileNextAction } from "../../helpers";
import { findRelatedProfiles } from "../../helpers/companyGroups";
import {
    clearCachedClient360Profile,
    readClient360NavigationState,
    readSeedDatasetForProfile,
    writeCachedClient360Profile,
} from "../navigationState";

type MinimalUser = {
    role: string;
} | null | undefined;

export function useClient360ProfileData({
    customerFromParams,
    isCompact,
    profileId,
    user,
}: {
    customerFromParams: string;
    isCompact: boolean;
    profileId: string;
    user: MinimalUser;
}) {
    const location = useLocation();
    const seedProfile = readClient360NavigationState(location.state).seedProfile ?? null;
    const seedDataset = useMemo(
        () => readSeedDatasetForProfile(profileId, seedProfile),
        [profileId, seedProfile],
    );

    const profileQuery = useQuery({
        queryKey: ["client-360-profile", profileId],
        queryFn: () => fetchClientProfile(profileId),
        enabled: profileId !== "",
        retry: 1,
        staleTime: 60_000,
        placeholderData: (previous, previousQuery) => {
            if (previousQuery?.queryKey?.[1] === profileId) {
                return previous ?? seedDataset;
            }

            return seedDataset;
        },
    });

    const profile = useMemo(
        () => findProfileInDataset(profileQuery.data?.profiles, profileId),
        [profileId, profileQuery.data?.profiles],
    );

    const profileReady =
        profileQuery.isSuccess
        && !profileQuery.isError
        && profileQuery.isFetched
        && profile !== null;

    useEffect(() => {
        if (!profileReady || profileId === "" || !profileQuery.data) {
            return;
        }

        writeCachedClient360Profile(profileId, profileQuery.data);
    }, [profileId, profileQuery.data, profileReady]);

    useEffect(() => {
        if (profileQuery.isError) {
            clearCachedClient360Profile(profileId);
        }
    }, [profileId, profileQuery.isError]);

    const historyCustomer = profile?.customer ?? customerFromParams;

    const relatedQuery = useQuery({
        queryKey: ["client-360-profile-related", profileId],
        queryFn: () => fetchClientProfileRelated(profileId),
        enabled: profileReady,
        retry: 1,
        staleTime: 60_000,
    });

    const cdpQuery = useQuery({
        queryKey: ["client-360-profile-cdp", profileId],
        queryFn: () => fetchClientProfileCdp(profileId),
        enabled: profileReady,
        retry: 1,
        staleTime: 120_000,
    });

    const historyQuery = useQuery({
        queryKey: ["client-360-history", profileId, "light", "orders", historyCustomer],
        queryFn: () => fetchClientHistory(profileId, historyCustomer, { light: true, ordersOnly: true }),
        enabled: profileReady,
        retry: 1,
        staleTime: 60_000,
    });

    const historyExtrasQuery = useQuery({
        queryKey: ["client-360-history-extras", profileId, historyCustomer],
        queryFn: () => fetchClientHistoryExtras(profileId, historyCustomer),
        enabled: profileReady,
        retry: 1,
        staleTime: 60_000,
    });

    const selectedData = useMemo(() => {
        if (!profileReady || !profileQuery.data || !profile) {
            return null;
        }

        const profiles = [
            ...(profileQuery.data.profiles ?? []),
            ...(relatedQuery.data?.profiles ?? []),
        ];
        const loyaltyRows = profileQuery.data.loyaltyRows ?? [];
        const issues = profileQuery.data.issues ?? [];
        const selectedOrders = historyQuery.data?.orders ?? [];
        const selectedLoyaltyCard = selectLoyaltyCard(profile, loyaltyRows);
        const selectedIssues = issues.filter((row) =>
            [profile.profileId, profile.loyaltyCard, ...selectedOrders.map((order) => order.orderNumber)].includes(row.entity)
        );
        const manualRelatedProfiles = (historyExtrasQuery.data?.manualProfileLinks ?? [])
            .map((item) => {
                const linkedProfile = findProfileInDataset(profiles, item.linkedProfileId);

                return linkedProfile ? { profile: linkedProfile, note: item.note, createdAt: item.createdAt } : null;
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
        const manualIds = new Set(manualRelatedProfiles.map((item) => item.profile.profileId));

        return {
            canManageRelatedProfiles: historyExtrasQuery.data?.canManageProfileLinks ?? Boolean(
                user && ["administrator", "sales_supervisor", "management"].includes(user.role)
            ),
            manualRelatedProfiles,
            nextAction: getProfileNextAction(profile, selectedIssues.length),
            profiles,
            profile,
            relatedProfiles: findRelatedProfiles(profiles, profile).filter((row) => !manualIds.has(row.profileId)),
            selectedIssues,
            selectedLoyaltyCard,
            selectedOrders,
            traderDefect: historyExtrasQuery.data?.traderDefect ?? null,
        };
    }, [historyExtrasQuery.data, historyQuery.data, profile, profileQuery.data, profileReady, relatedQuery.data, user]);

    const detailSections = useMemo(() => {
        if (!selectedData?.nextAction) {
            return [];
        }

        const visibleOrders = isCompact ? selectedData.selectedOrders.slice(0, 2) : selectedData.selectedOrders.slice(0, 6);
        const visibleIssues = isCompact ? selectedData.selectedIssues.slice(0, 2) : selectedData.selectedIssues.slice(0, 4);

        return buildClientDetailSections({
            hiddenIssueCount: Math.max(selectedData.selectedIssues.length - visibleIssues.length, 0),
            hiddenOrderCount: Math.max(selectedData.selectedOrders.length - visibleOrders.length, 0),
            orders: selectedData.selectedOrders,
            visibleIssues,
            visibleOrders,
        });
    }, [isCompact, selectedData]);

    const subsidiaryLoadError = useMemo(() => {
        if (!profileReady) {
            return false;
        }

        return (
            relatedQuery.isError
            || cdpQuery.isError
            || historyQuery.isError
            || historyExtrasQuery.isError
        );
    }, [cdpQuery.isError, historyExtrasQuery.isError, historyQuery.isError, profileReady, relatedQuery.isError]);

    return {
        cdpQuery,
        detailSections,
        historyExtrasQuery,
        historyQuery,
        profileQuery,
        profileReady,
        relatedQuery,
        selectedData,
        subsidiaryLoadError,
    };
}

function selectLoyaltyCard(profile: MasterProfile, loyaltyRows: LoyaltyRow[]) {
    const partnerIdSuffix = profile.profileId.match(/(\d+)$/)?.[1] ?? "";
    const canonicalProfileId = profileIdFromPartnerId(Number.parseInt(partnerIdSuffix, 10));

    return loyaltyRows.find((row) => canonicalProfileId !== null && row.profileId === canonicalProfileId)
        ?? loyaltyRows.find((row) => row.profileId === `PRT-${partnerIdSuffix}`)
        ?? loyaltyRows.find((row) => row.cardNumber === profile.loyaltyCard && profile.loyaltyCard !== "")
        ?? loyaltyRows.find((row) => row.holder === profile.customer)
        ?? null;
}
